import React, { useState } from 'react';
import { Button, Frame, ScrollView, Separator, TextInput, Toolbar } from 'react95';
import { useTheme } from 'styled-components';
import imageFileIcon from '@/assets/icons/image_viewer.png';
import folderIcon from '@/assets/icons/folder_closed.png';
import { galleryImages } from '@/desktop/content/imageGallery';
import '@/desktop/appearance/IconSelection.css';
import './FolderApp.css';
import { DEFAULT_DESKTOP_SETTINGS } from '@/desktop/state/desktopStorage';

export default function FolderApp({ onOpenApp, desktopSettings = DEFAULT_DESKTOP_SETTINGS, windowActive = true }) {
  const theme = useTheme();
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState('icons');
  const selectedImage = galleryImages.find(image => image.id === selectedId);

  function openImage(startIndex) {
    onOpenApp('image-viewer', { source: 'My Folder', images: galleryImages, startIndex });
  }

  return (
    <section className="folder-app" aria-label="My Folder files" data-window-active={windowActive} style={{
      '--folder-field': theme.canvas,
      '--folder-text': theme.canvasText,
      '--desktop-accent-dark': theme.borderDark,
      '--folder-inactive': theme.material,
      '--folder-button-text': theme.materialText,
      '--folder-button-light': theme.borderLightest,
      '--folder-button-dark': theme.borderDark
    }}>
      <Toolbar className="folder-menu" aria-label="Folder menu">
        {['File', 'Edit', 'View', 'Help'].map(label => (
          <Button variant="thin" type="button" className="folder-toolbar-button" key={label}>
            <span className="folder-menu-mnemonic">{label[0]}</span>{label.slice(1)}
          </Button>
        ))}
      </Toolbar>
      <Separator className="folder-toolbar-divider" aria-hidden="true" />
      <Toolbar className="folder-path" aria-label="Folder view controls">
        <div className="folder-location">
          <img src={folderIcon} alt="" />
          <TextInput aria-label="Address" className="folder-address" value="C:\My Folder" readOnly />
        </div>
        <Separator orientation="vertical" size={30} />
        <Button variant="thin" type="button" className="folder-toolbar-button folder-view-button" active={view === 'icons'} aria-pressed={view === 'icons'}
          aria-label="Icons view" title="Icons" onClick={() => setView('icons')}>
          <svg className="folder-view-icon" viewBox="0 0 16 16" aria-hidden="true">
            <g fill="none" stroke="currentColor" strokeWidth="1" shapeRendering="crispEdges">
              <path d="M2.5 1.5h4v4h-4zM9.5 1.5h4v4h-4zM2.5 9.5h4v4h-4zM9.5 9.5h4v4h-4z" />
              <path d="M1.5 7.5h6M8.5 7.5h6M1.5 15.5h6M8.5 15.5h6" />
            </g>
          </svg>
        </Button>
        <Button variant="thin" type="button" className="folder-toolbar-button folder-view-button" active={view === 'thumbnails'} aria-pressed={view === 'thumbnails'}
          aria-label="Thumbnails view" title="Thumbnails" onClick={() => setView('thumbnails')}>
          <svg className="folder-view-icon" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M1 1h14v14H1z" fill="none" stroke="currentColor" />
            <path d="m2 13 4-6 3 4 2-3 3 5z" fill="currentColor" />
            <circle cx="11" cy="5" r="2" fill="currentColor" />
          </svg>
        </Button>
      </Toolbar>
      <ScrollView className="folder-content" shadow={false} onClick={event => {
        if (!event.target.closest('.folder-file')) setSelectedId(null);
      }}>
        <div className={`folder-files is-${view}`}>
          {galleryImages.map((image, startIndex) => (
            <button type="button" className="folder-file selectable-icon" key={image.id}
              data-selected={selectedId === image.id}
              data-icon-glow={windowActive ? desktopSettings.iconGlowEffect : undefined}
              aria-pressed={selectedId === image.id}
              onClick={() => setSelectedId(image.id)}
              onDoubleClick={() => openImage(startIndex)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  setSelectedId(image.id);
                  openImage(startIndex);
                }
              }}>
              <img src={view === 'icons' ? imageFileIcon : image.src} alt="" />
              <span className="folder-file-name">{image.fileName}</span>
            </button>
          ))}
        </div>
      </ScrollView>
      <footer className="folder-status" role="status">
        <Frame variant="status" className="folder-count">{selectedImage ? selectedImage.fileName : `${galleryImages.length} objects`}</Frame>
        <Frame variant="status" className="folder-type">{selectedImage ? 'PNG image' : 'My Folder'}</Frame>
      </footer>
    </section>
  );
}
